import {
    Card,
    Text,
    Persona,
    TableBody,
    TableCell,
    TableRow,
    Table,
    TableHeader,
    TableHeaderCell,
    Button,
    Switch,
    makeStyles,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";

import { useSignInState } from './Components/signInState';
import useUpdateThemePreference from './Components/updateThemePreference';
import { useAuth } from './context/AuthContext';

type ReadOnlyProfileField = 'DisplayName' | 'FirstName' | 'LastName' | 'PhoneNumber';

const useStyles = makeStyles({
    profileCardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
});

const fieldLabels: Record<ReadOnlyProfileField, string> = {
    DisplayName: "Display Name",
    FirstName: "First Name",
    LastName: "Last Name",
    PhoneNumber: "Phone Number",
};

const profilePage = () => {
    const { isAuthenticated } = useAuth();
    const styles = useStyles();
    const { signInState, setSignInState } = useSignInState();
    const { updateThemePreference } = useUpdateThemePreference();

    const userInfo = signInState.userInfo;

    if (!isAuthenticated) {
        console.log('[ProfilePage] No session, user not logged in.');
        return <div>Please log in to view your profile.</div>;
    }

    if (!userInfo) {
        return <div>Loading profile...</div>;
    }

    // Switch label is "Light Mode": checked => light, unchecked => dark. Polarity flipped
    // versus the old "Dark Mode" switch so the theme default ("dark") corresponds to the
    // unchecked / off state.
    const switchTheme = (checked: boolean) => {
        const themePreference = checked ? "light" : "dark";
        void updateThemePreference(themePreference);
        setSignInState((prevState) => prevState.userInfo
            ? { ...prevState, userInfo: { ...prevState.userInfo, ThemePreference: themePreference } }
            : prevState);
    };

    const displayName = userInfo.DisplayName
        || `${userInfo.FirstName ?? ''} ${userInfo.LastName ?? ''}`.trim()
        || userInfo.Email;

    return (
        <div style={{ margin: '20px' }}>
            <Card>
                <div className={styles.profileCardHeader}>
                    <Text size={600} weight="semibold">User Profile</Text>
                    <Button
                        appearance="subtle"
                        aria-label="Close"
                        icon={<DismissRegular />}
                        onClick={() => globalThis.history.back()}
                    />
                </div>
                <Persona
                    key={userInfo.IdentityUserID}
                    name={displayName}
                    secondaryText={userInfo.Email}
                    size={"huge"}
                />
                <Switch
                    key="ThemePreference"
                    label={"Light Mode"}
                    defaultChecked={userInfo.ThemePreference === "light"}
                    labelPosition="after"
                    onChange={(_event, checked) => { switchTheme(checked.checked) }}
                />
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>User Info</TableHeaderCell>
                            <TableHeaderCell>Value</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(["DisplayName", "FirstName", "LastName", "PhoneNumber"] as ReadOnlyProfileField[]).map((key) => (
                            <TableRow key={key}>
                                <TableCell>{fieldLabels[key]}</TableCell>
                                <TableCell>{userInfo[key] ?? ''}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

            </Card>
        </div>
    );
};

export default profilePage;
